import {
  NOTIFICATION_DELIVERY_STATUS_LABELS,
  NOTIFICATION_DELIVERY_STATUSES,
  NOTIFICATION_KIND_GROUPS,
  NOTIFICATION_KIND_LABELS,
  type NotificationDeliveryStatus,
  type NotificationKind,
  notificationDeliveryStatusSchema,
  notificationKindSchema,
} from '@repo/api-contracts';
import { type ReactElement, useId } from 'react';
import { Button, FilterSelect, Input, SearchInput } from '@/shared/ui';

interface NotificationDeliveryFiltersProps {
  readonly kind: NotificationKind | null;
  readonly status: NotificationDeliveryStatus | null;
  readonly fromDate: string;
  readonly toDate: string;
  readonly email: string;
  readonly emailError: string | null;
  /** 기간이 거꾸로면(시작일 > 종료일) 안내 문구. 있으면 두 날짜 칸을 틀린 값으로 표시하고 이 문구로 설명한다. */
  readonly dateError: string | null;
  readonly onKind: (kind: NotificationKind | null) => void;
  readonly onStatus: (status: NotificationDeliveryStatus | null) => void;
  readonly onFromDate: (date: string) => void;
  readonly onToDate: (date: string) => void;
  readonly onEmail: (value: string) => void;
  readonly onSubmit: () => void;
}

const DATE_INPUT_CLASS = 'h-10 w-40';

/**
 * 발송 이력 도구줄. 종류(화면 묶음 optgroup + 세부 종류)·결과·기간은 고르는 즉시 적용한다. 수신 이메일은 검색 버튼(또는 Enter)으로만
 * 적용한다. 목록 조회마다 서버가 관리자 접속기록을 남기므로 입력마다 보내지 않는다. 기간은 한국 달력 날짜다. 안내 문구(기간·이메일)는
 * `aria-describedby` 로 해당 칸에 잇고 그 칸을 틀린 값(`hasError` → `aria-invalid`)으로 표시한다.
 */
export const NotificationDeliveryFilters = ({
  kind,
  status,
  fromDate,
  toDate,
  email,
  emailError,
  dateError,
  onKind,
  onStatus,
  onFromDate,
  onToDate,
  onEmail,
  onSubmit,
}: NotificationDeliveryFiltersProps): ReactElement => {
  const id = useId();
  const dateErrorId = `${id}-date-error`;
  const emailErrorId = `${id}-email-error`;

  return (
    <form
      aria-label="알림 발송 이력 검색"
      noValidate
      className="flex min-w-0 flex-1 flex-wrap items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <FilterSelect
        aria-label="종류"
        value={kind ?? ''}
        onChange={(event) => {
          const parsed = notificationKindSchema.safeParse(event.target.value);
          onKind(parsed.success ? parsed.data : null);
        }}
      >
        <option value="">전체 종류</option>
        {NOTIFICATION_KIND_GROUPS.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.kinds.map((value) => (
              <option key={value} value={value}>
                {NOTIFICATION_KIND_LABELS[value]}
              </option>
            ))}
          </optgroup>
        ))}
      </FilterSelect>
      <FilterSelect
        aria-label="결과"
        value={status ?? ''}
        onChange={(event) => {
          const parsed = notificationDeliveryStatusSchema.safeParse(event.target.value);
          onStatus(parsed.success ? parsed.data : null);
        }}
      >
        <option value="">전체 결과</option>
        {NOTIFICATION_DELIVERY_STATUSES.map((value) => (
          <option key={value} value={value}>
            {NOTIFICATION_DELIVERY_STATUS_LABELS[value]}
          </option>
        ))}
      </FilterSelect>
      <Input
        type="date"
        aria-label="시작일"
        hasError={dateError !== null}
        aria-describedby={dateError === null ? undefined : dateErrorId}
        className={DATE_INPUT_CLASS}
        value={fromDate}
        onChange={(event) => onFromDate(event.target.value)}
      />
      <span aria-hidden="true" className="text-muted-foreground">
        ~
      </span>
      <Input
        type="date"
        aria-label="종료일"
        hasError={dateError !== null}
        aria-describedby={dateError === null ? undefined : dateErrorId}
        className={DATE_INPUT_CLASS}
        value={toDate}
        onChange={(event) => onToDate(event.target.value)}
      />
      <SearchInput
        aria-label="수신 이메일 검색"
        placeholder="수신 이메일(정확히 일치)"
        hasError={emailError !== null}
        aria-describedby={emailError === null ? undefined : emailErrorId}
        value={email}
        onChange={(event) => onEmail(event.target.value)}
      />
      <Button type="submit" variant="outline" className="h-10 px-5">
        검색
      </Button>
      {dateError ? (
        <p id={dateErrorId} role="alert" className="w-full text-xs text-destructive">
          {dateError}
        </p>
      ) : null}
      {emailError ? (
        <p id={emailErrorId} role="alert" className="w-full text-xs text-destructive">
          {emailError}
        </p>
      ) : null}
    </form>
  );
};
